// src/pages/departments/CivilEngineering.js
//
// Civil Engineering departmental page using the FPB departmental UI standard.
// Departmental narrative, HOD identity, admission requirements, course structure,
// contact details and brochure copy are based on the supplied "Civil Eng. website.docx".
// Staff academic data are extracted separately from the Civil Engineering staff CSV.
//
// EDITORIAL NOTE:
// The supplied admission paragraph accidentally says "Chemical Engineering Technology".
// Because the document is the Civil Engineering departmental write-up and the surrounding
// content is explicitly Civil Engineering, the programme name is normalised here to
// "Civil Engineering Technology" while the substantive requirements are preserved.

import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Award,
  BadgeCheck,
  BookOpen,
  BookOpenCheck,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Construction,
  Download,
  DraftingCompass,
  FileText,
  GraduationCap,
  HardHat,
  Mail,
  MapPin,
  Maximize2,
  Phone,
  Route,
  Ruler,
  Search,
  ShieldCheck,
  Users,
  Users2,
  Waves,
  X,
} from "lucide-react";

import {
  CIVIL_STAFF_IMAGE_BASE,
  civilEngineeringStaff,
} from "./civilEngineeringStaffData";

/* =========================================================
   ASSET PATHS
========================================================= */

const CIVIL_IMAGE_BASE = "/images/departments/civil-engineering";

/* =========================================================
   HELPERS
========================================================= */

const Divider = ({ delay = 0 }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    transition={{ duration: 0.75, delay }}
    viewport={{ once: true, amount: 0.1 }}
    className="origin-left h-1 bg-gradient-to-r from-blue-700 to-blue-400 rounded-full my-8"
  />
);

const EMPTY_VALUES = new Set([
  "",
  "nil",
  "nill",
  "none",
  "n/a",
  "na",
  "not applicable",
  "not available",
  "-",
]);

const cleanText = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  return EMPTY_VALUES.has(text.toLowerCase()) ? "" : text;
};

const cleanArray = (items = []) =>
  [...new Set(items.map(cleanText).filter(Boolean))];

const fullStaffName = (staff) =>
  `${cleanText(staff?.title)} ${cleanText(staff?.name)}`.trim();

/* =========================================================
   HOD — SUPPLIED DEPARTMENTAL WRITE-UP
========================================================= */

const hodProfile = {
  name: "Engr. Yabagi Isah Abudullahi",
  credentials: "NATE, MNSE, COREN",
  qualification: "M.Sc Civil Engineering (A.B.U Zaria)",
  role: "Head of Department",
  image: `${CIVIL_STAFF_IMAGE_BASE}/yabagi-isah-abdullahi.jpg`,
  email: "civil.engineering@fedpolybida.edu.ng",
  phone: "+234 803 356 6533",
};

/* =========================================================
   WHY STUDY CIVIL ENGINEERING
========================================================= */

const whyStudyIntro =
  "Studying Civil Engineering Technology in our Polytechnic gives students the opportunity to combine strong academic knowledge with practical, industry-relevant skills. Our programme is designed to prepare students for real-world engineering challenges through hands-on training, modern technology, and professional development.";

const studyReasons = [
  {
    icon: Construction,
    title: "Practical Learning",
    description:
      "Students gain valuable experience through laboratory work, field exercises, workshops, technical projects, and industrial training.",
  },
  {
    icon: DraftingCompass,
    title: "Industry-Relevant Skills",
    description:
      "Our curriculum develops the technical and professional skills required in construction, structural works, highways, water resources, surveying, and other areas of civil engineering.",
  },
  {
    icon: Ruler,
    title: "Modern Engineering Exposure",
    description:
      "Students are introduced to contemporary engineering tools, digital applications, modern surveying techniques, and emerging construction technologies.",
  },
  {
    icon: Users2,
    title: "Experienced Academic Staff",
    description:
      "Our lecturers and instructors are committed to providing quality teaching, mentorship, and practical guidance throughout the programme.",
  },
  {
    icon: Building2,
    title: "Career Opportunities",
    description:
      "Graduates are prepared for employment in construction companies, engineering firms, government agencies, consulting organisations, and infrastructure development projects.",
  },
  {
    icon: HardHat,
    title: "Entrepreneurship Development",
    description:
      "Students are encouraged to develop innovative and entrepreneurial skills that can help them establish engineering and construction-related businesses.",
  },
  {
    icon: GraduationCap,
    title: "Preparation for Further Studies",
    description:
      "The programme provides a strong foundation for graduates who wish to advance their education and specialise in different areas of Civil Engineering.",
  },
];

const whyStudyClosing =
  "At our Polytechnic, we do not only prepare students to earn a qualification; we prepare them to become competent professionals capable of solving practical problems, creating sustainable infrastructure, and contributing meaningfully to national development.";

/* =========================================================
   JOB PROSPECTS
========================================================= */

const jobProspectsIntro =
  "Graduates of Civil Engineering Technology from The Federal Polytechnic, Bida have access to a wide range of career opportunities in the construction, infrastructure, transportation, environmental, and public works sectors. Their practical and technical training prepares them to contribute effectively to the planning, construction, supervision, maintenance, and management of engineering projects.";

const careerOpportunities = [
  "Construction Technologist",
  "Site Engineer/Technologist",
  "Structural Engineering Assistant",
  "Highway and Transportation Technologist",
  "Building and Construction Supervisor",
  "Quantity and Project Support Officer",
  "Water Resources Technologist",
  "Geotechnical Engineering Assistant",
  "Surveying and Mapping Assistant",
  "Materials Testing and Quality Control Officer",
  "CAD Technician/Draughtsman",
  "Environmental and Drainage Officer",
  "Project Management Assistant",
  "Works and Maintenance Officer",
  "Health, Safety and Environment Officer",
  "Technical Officer in Government Ministries and Agencies",
  "Engineering Laboratory Technologist",
  "Infrastructure Maintenance Officer",
  "Construction Consultant",
  "Entrepreneur/Construction Contractor",
];

const jobProspectsClosing =
  "Civil Engineering Technology graduates can work in construction, consultancy, government, transportation, water, property, oil and gas, manufacturing, education, and sectors. With further training, certification, experience, and education, they can advance into senior, managerial, consultancy, and specialised roles. Graduates can also become entrepreneurs in construction, maintenance, materials supply, and infrastructure services.";

/* =========================================================
   PROFESSIONAL DEVELOPMENT
========================================================= */

const professionalDevelopmentIntro =
  "For Civil Engineering Technology students and graduates from The Federal Polytechnic Bida, professional development usually falls into two groups: professional registration and industry/technical certifications.";

const professionalRegistration =
  "For professional registration in Nigeria, the most important pathway is through the Council for the Regulation of Engineering in Nigeria (COREN). COREN recognizes Polytechnic qualifications for the appropriate engineering cadre. For example, an ND holder may pursue registration as an Engineering Technician after the required experience, while an HND holder from a COREN-accredited programme may pursue registration as an Engineering Technologist after meeting the prescribed professional experience and examination requirements.";

const professionalAssociationNote =
  "Graduates can also pursue membership and professional development through engineering associations such as the Nigerian Society of Engineers and relevant specialist institutions.";

const professionalCourses = [
  {
    title: "AutoCAD for Design and Drafting",
    description:
      "Useful for engineering drawings, plans, sections and detailing. Autodesk offers recognized AutoCAD certification pathways.",
  },
  {
    title: "Autodesk Civil 3D",
    description:
      "Particularly valuable for road design, profiles, alignments, grading, drainage, earthworks and infrastructure projects. Autodesk currently offers the Certified Professional in Civil 3D for Infrastructure Design.",
  },
  {
    title: "Building Information Modelling (BIM)",
    description:
      "Training in Revit, Navisworks and related BIM workflows for modern construction projects.",
  },
  {
    title: "Structural Design Software",
    description:
      "Courses in applications such as STAAD.Pro, ETABS, SAP2000 or similar structural analysis packages.",
  },
  {
    title: "Project Management",
    description:
      "Courses in construction and engineering project planning. Experienced professionals can work toward PMP certification from the Project Management Institute, although PMP has education, training and professional-experience requirements.",
  },
  {
    title: "Primavera P6 / Microsoft Project",
    description:
      "Useful for construction planning, scheduling, resource allocation and project monitoring.",
  },
  {
    title: "Geographic Information Systems (GIS)",
    description:
      "ArcGIS or QGIS training is valuable for surveying, infrastructure planning, transportation and environmental projects. Esri also provides formal GIS certification pathways.",
  },
  {
    title: "Quantity Estimation and Costing",
    description:
      "Preparation of Bills of Engineering Measurement and Evaluation (BEME), material estimates and construction budgets.",
  },
  {
    title: "Construction Management",
    description:
      "Site management, contract administration, procurement, supervision and quality management.",
  },
  {
    title: "Health, Safety and Environment (HSE)",
    description:
      "HSE Levels 1–3, construction safety, risk assessment, first aid and occupational safety courses.",
  },
  {
    title: "QA/QC for Civil Engineering",
    description:
      "Quality assurance, quality control, concrete testing, soil testing, materials inspection and documentation.",
  },
  {
    title: "Surveying and Geomatics",
    description:
      "Total Station, GPS/GNSS, levelling, drone surveying and digital mapping.",
  },
];


/* =========================================================
   COURSE STRUCTURE — SUPPLIED DEPARTMENTAL WRITE-UP
========================================================= */

const ndCourseStructure = [
  "Basic Principles in Surveying",
  "Structural Mechanics",
  "Introduction to Fluid Mechanics",
  "Workshop Technology",
  "Civil Engineering Construction",
  "Engineering Geology and Basic Soil Mechanics",
  "Introductory Hydrology",
  "Science and Properties of Materials",
  "Strength of Materials",
  "Engineering Survey I",
  "Hydraulics and Hydrology",
  "Civil Engineering Drawing",
  "Theory of Structures",
  "Hydro-Geology",
  "Elements of Geo-Informatics",
  "Student Industrial Work Experience Scheme",
  "Water Supply and Sanitary Engineering",
  "Introduction to Highway Engineering",
  "Introduction to Structural Design",
  "Soil Science and Irrigation Engineering",
  "Soil Mechanics",
  "Engineering Measurement and Evaluation",
  "Estimating and Costing of Construction Materials",
  "Construction Management",
  "Project",
];

const hndCourseStructure = [
  "Engineering Survey",
  "Civil Engineering Quantities and Specifications",
  "Hydraulics",
  "Concrete Technology",
  "Theory of Structures",
  "Computer Aided Design and Drafting in Civil Engineering",
  "Design of Structural Elements",
  "Soil Mechanics",
  "Hydrology and Hydro-Geology",
  "Water and Waste Water Engineering",
  "Foundation Engineering",
  "Transportation Engineering",
  "Estimating and Costing of Construction Materials",
  "GIS Data Base Creation & Usage",
  "Industrial Management",
  "Project and Research Method",
  "Traffic Engineering",
  "Design in Structural Steel and Timber",
  "Highway Engineering",
  "Foundation Design",
  "Advanced Reinforced and Pre-Stressed Concrete Design",
  "Hydraulic Structures",
  "Environmental Engineering",
  "Infrastructure Planning and Management",
  "Alternative Transportation Engineering",
  "Irrigation and Drainage",
  "Engineering Management",
  "Water Resources Management",
  "Matrix and Energy Method in Engineering",
  "Geotechnical Engineering",
  "Transportation Planning",
];

/* =========================================================
   BROCHURE CONTENT
========================================================= */

const brochureCards = [
  {
    icon: Building2,
    title: "Established Civil Engineering Training",
    text: "The Department was established in 1977 and currently runs National Diploma and Higher National Diploma programmes, with HND options in Structures, Transportation and Water.",
  },
  {
    icon: Ruler,
    title: "Core Areas of Study",
    text: "Students are exposed to structural engineering, highway and transportation engineering, geotechnical engineering, water resources, construction technology, surveying, engineering drawing, environmental engineering and project management.",
  },
  {
    icon: HardHat,
    title: "Practical Learning",
    text: "Training combines classroom instruction with laboratory practicals, field exercises, workshops, technical projects, computer applications and industrial training.",
  },
  {
    icon: DraftingCompass,
    title: "Modern Engineering Exposure",
    text: "Students are introduced to computer-aided design, Building Information Modelling, Geographic Information Systems, modern surveying equipment, drones and sustainable construction techniques.",
  },
  {
    icon: BriefcaseBusiness,
    title: "Career Opportunities",
    text: "Graduates can work in construction companies, engineering consultancy firms, government ministries and agencies, transportation organisations, water corporations, property development companies and environmental organisations.",
  },
  {
    icon: GraduationCap,
    title: "Professional & Further Development",
    text: "The programme prepares graduates for professional development, entrepreneurship and further study in specialised areas of Civil Engineering.",
  },
]

/* =========================================================
   GALLERY
========================================================= */

const galleryImages = [
  {
    img: "civil-gallery1.jpg",
  },
  {
    img: "civil-gallery2.jpg",
  },
  {
    img: "civil-gallery3.jpg",
  },
  {
    img: "civil-gallery4.jpg",
  },
  {
    img: "civil-gallery5.jpg",
    
  },
];

/* =========================================================
   STAFF PROFILE MODAL
========================================================= */

const StaffAcademicProfileModal = ({ staff, onClose }) => {
  if (!staff) return null;

  const qualifications = cleanArray(staff.qualifications);
  const memberships = cleanArray(staff.professionalQualifications);
  const research = cleanArray(staff.researchInterests);
  const specialties = cleanArray(staff.specialties);
  const awards = cleanArray(staff.awards);
  const publications = cleanText(staff.publications);
  const email = cleanText(staff.email);
  const phone = cleanText(staff.phone);
  const name = fullStaffName(staff);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-3 sm:p-5 md:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 18 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="civil-staff-profile-title"
        className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto rounded-2xl md:rounded-3xl bg-white shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close staff academic profile"
          className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:bg-slate-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
        >
          <X size={21} />
        </button>

        <div className="grid md:grid-cols-[340px_minmax(0,1fr)]">
          <aside className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-blue-950 to-blue-800 p-7 pt-16 text-white md:p-8">
            <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-blue-400/10 blur-3xl" />

            <div className="relative z-10">
              <div className="mx-auto aspect-[4/5] w-full max-w-[280px] overflow-hidden rounded-2xl border-4 border-white/20 bg-slate-200 shadow-2xl">
                <img
                  src={staff.image}
                  alt={name}
                  className="h-full w-full object-cover object-top"
                  onError={(event) => {
                    event.currentTarget.src = "/images/default-staff.jpg";
                  }}
                />
              </div>

              <div className="mt-5 flex justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold text-blue-50">
                  <BadgeCheck size={15} />
                  {cleanText(staff.rank) || "Department Staff"}
                </span>
              </div>

              <div className="mt-6 text-center md:text-left">
                <h2 className="text-2xl font-extrabold leading-tight">{name}</h2>
                <p className="mt-2 text-sm font-medium text-blue-200">
                  Department of Civil Engineering
                </p>
              </div>

              {email && (
                <a
                  href={`mailto:${email}`}
                  className="mt-5 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100 transition hover:bg-white/10"
                >
                  <Mail size={18} className="shrink-0 text-blue-300" />
                  <span className="break-all">{email}</span>
                </a>
              )}

              {phone && (
                <a
                  href={`tel:${phone.replace(/\s+/g, "")}`}
                  className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100 transition hover:bg-white/10"
                >
                  <Phone size={18} className="shrink-0 text-blue-300" />
                  <span>{phone}</span>
                </a>
              )}
            </div>
          </aside>

          <div className="min-w-0 p-6 sm:p-8 md:p-10 lg:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              Department Staff
            </p>
            <h3
              id="civil-staff-profile-title"
              className="mt-2 text-2xl font-extrabold text-slate-900 sm:text-3xl"
            >
              Staff Academic Profile
            </h3>
            <div className="mt-4 h-1 w-16 rounded-full bg-blue-700" />

            {qualifications.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <GraduationCap size={20} className="text-blue-700" />
                  Academic Qualifications
                </h4>
                <div className="mt-4 space-y-3">
                  {qualifications.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                    >
                      <CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />
                      <p className="text-sm leading-6 text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {memberships.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <ShieldCheck size={19} className="text-blue-700" />
                  Professional Qualifications
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {memberships.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {publications && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <BookOpen size={19} className="text-blue-700" />
                  Academic Publications
                </h4>
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700">
                  {publications}
                </div>
              </section>
            )}

            {research.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <BookOpenCheck size={19} className="text-blue-700" />
                  Research Interests
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {research.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {specialties.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <DraftingCompass size={19} className="text-blue-700" />
                  Areas of Specialty
                </h4>
                <div className="mt-4 space-y-3">
                  {specialties.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-xl border border-slate-200 px-4 py-3 text-sm leading-6 text-slate-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {awards.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <Award size={19} className="text-amber-600" />
                  Academic Awards / Breakthroughs
                </h4>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {awards.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/70 p-4"
                    >
                      <Award size={18} className="mt-0.5 shrink-0 text-amber-600" />
                      <span className="text-sm font-medium leading-6 text-slate-700">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   GALLERY LIGHTBOX
========================================================= */

const GalleryLightbox = ({ image, images, onClose, onChange }) => {
  if (!image) return null;

  const currentIndex = image.index;

  const changeImage = (direction) => {
    const nextIndex =
      direction === "next"
        ? currentIndex === images.length - 1
          ? 0
          : currentIndex + 1
        : currentIndex === 0
          ? images.length - 1
          : currentIndex - 1;

    onChange({ ...images[nextIndex], index: nextIndex });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/95 p-3 backdrop-blur-md sm:p-5 md:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="relative w-full max-w-6xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close gallery"
          className="absolute right-3 top-3 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-800 shadow-xl hover:text-blue-700"
        >
          <X size={22} />
        </button>

        <button
          type="button"
          onClick={() => changeImage("previous")}
          aria-label="Previous image"
          className="absolute left-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-xl hover:text-blue-700 md:left-5"
        >
          <ChevronLeft size={27} />
        </button>

        <button
          type="button"
          onClick={() => changeImage("next")}
          aria-label="Next image"
          className="absolute right-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-xl hover:text-blue-700 md:right-5"
        >
          <ChevronRight size={27} />
        </button>

        <div className="overflow-hidden rounded-2xl bg-black shadow-2xl">
          <div className="flex min-h-[260px] items-center justify-center bg-black">
            <motion.img
              key={image.img}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              src={`${CIVIL_IMAGE_BASE}/gallery/${image.img}`}
              alt={image.title}
              className="max-h-[78vh] w-full object-contain"
              onError={(event) => {
                event.currentTarget.src = `${CIVIL_IMAGE_BASE}/civil-engineering-hero.jpg`;
              }}
            />
          </div>

          <div className="bg-slate-900 px-5 py-4 text-white">
            <h3 className="font-semibold">{image.title}</h3>
            <p className="mt-1 text-sm text-slate-300">{image.caption}</p>
            <p className="mt-1 text-xs text-slate-500">
              Image {currentIndex + 1} of {images.length}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const CivilEngineering = () => {
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [staffSearch, setStaffSearch] = useState("");

  const filteredStaff = useMemo(() => {
    const query = staffSearch.trim().toLowerCase();
    if (!query) return civilEngineeringStaff;

    return civilEngineeringStaff.filter((staff) => {
      const haystack = [
        fullStaffName(staff),
        cleanText(staff.rank),
        ...cleanArray(staff.researchInterests),
        ...cleanArray(staff.specialties),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [staffSearch]);

  const fadeUp = {
    hidden: { opacity: 1, y: 18 },
    visible: { opacity: 1, y: 0 },
  };

  useEffect(() => {
    if (!selectedStaff && !selectedGalleryImage) return undefined;

    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedStaff(null);
        setSelectedGalleryImage(null);
      }

      if (selectedGalleryImage && event.key === "ArrowLeft") {
        const index = selectedGalleryImage.index;
        const nextIndex = index === 0 ? galleryImages.length - 1 : index - 1;
        setSelectedGalleryImage({ ...galleryImages[nextIndex], index: nextIndex });
      }

      if (selectedGalleryImage && event.key === "ArrowRight") {
        const index = selectedGalleryImage.index;
        const nextIndex = index === galleryImages.length - 1 ? 0 : index + 1;
        setSelectedGalleryImage({ ...galleryImages[nextIndex], index: nextIndex });
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedStaff, selectedGalleryImage]);

  return (
    <div className="min-h-screen bg-gray-50 text-slate-900">
      {/* DEPARTMENT NAVIGATION */}
      <nav className="sticky top-0 z-50 flex items-center gap-6 overflow-x-auto whitespace-nowrap bg-white/90 px-4 py-3 text-sm shadow-md backdrop-blur-md md:px-20">
        <div className="flex-1 font-semibold text-blue-700">
          Civil Engineering — Federal Polytechnic Bida
        </div>

        <div className="flex gap-4">
          <a href="#brief" className="hover:text-blue-500">Overview</a>
          <a href="#why" className="hover:text-blue-500">Why Study</a>
          <a href="#admission" className="hover:text-blue-500">Admission</a>
          <a href="#prospects" className="hover:text-blue-500">Careers</a>
          <a href="#certifications" className="hover:text-blue-500">Certifications</a>
          <a href="#staff" className="hover:text-blue-500">Meet Staff</a>
          <a href="#courses" className="hover:text-blue-500">Courses</a>
          <a href="#brochure" className="hover:text-blue-500">Brochure</a>
          <a href="#gallery" className="hover:text-blue-500">Gallery</a>
          <a href="#contact" className="hover:text-blue-500">Contact</a>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative flex h-[340px] items-center justify-center overflow-hidden md:h-[420px]">
        <img
          src={`${CIVIL_IMAGE_BASE}/ban.jpg`}
          alt="Civil Engineering infrastructure"
          className="absolute inset-0 h-full w-full scale-105 object-cover"
          onError={(event) => {
            event.currentTarget.src = "/images/department-hero.jpg";
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-blue-950/70 to-slate-950/40" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.25),transparent_35%)]" />

        <motion.div
          aria-hidden="true"
          className="absolute left-[7%] top-10 hidden h-16 w-16 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-blue-100 backdrop-blur-md md:flex"
          animate={{ y: [0, -8, 0], rotate: [0, 3, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
          <HardHat size={30} />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute right-[10%] top-14 hidden h-14 w-14 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-blue-100 backdrop-blur-md md:flex"
          animate={{ y: [0, 7, 0], rotate: [0, -4, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <DraftingCompass size={27} />
        </motion.div>

        <div className="relative z-10 px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-blue-50 backdrop-blur-md"
          >
            <Construction size={17} className="text-blue-200" />
            Civil Engineering Technology
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
            className="text-3xl font-extrabold md:text-5xl"
          >
            Department of Civil Engineering
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-3 max-w-3xl"
          >
            Building practical competence for safe, sustainable and resilient infrastructure through engineering education, field practice, research and innovation.
          </motion.p>

          <div className="mx-auto mt-5 flex max-w-3xl flex-wrap justify-center gap-2.5">
            {[
              { icon: Building2, label: "Structures" },
              { icon: Route, label: "Transportation" },
              { icon: Waves, label: "Water Resources" },
              { icon: DraftingCompass, label: "Design & Construction" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <span
                  key={item.label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/25 px-3 py-1.5 text-xs font-medium text-blue-50 backdrop-blur-sm sm:text-sm"
                >
                  <Icon size={15} className="text-blue-200" />
                  {item.label}
                </span>
              );
            })}
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a
              href="/admissions"
              className="rounded-lg bg-blue-700 px-5 py-3 font-semibold text-white shadow transition hover:bg-blue-600"
            >
              Apply Now
            </a>
            <a
              href="#brochure"
              className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-blue-700 shadow hover:bg-blue-50"
            >
              <FileText size={18} />
              View Brochure
            </a>
          </div>
        </div>
      </header>

      <main className="space-y-12 px-6 py-12 md:px-20">
        {/* DEPARTMENT IN BRIEF */}
        <motion.section
          id="brief"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <BookOpen className="text-blue-700" />
            The Department in Brief
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-700">
            The Department of Civil Engineering was established in 1977. At the early stage, the department housed the Civil, Architecture and Quantity Surveying Sections under one Head of Department. In the 1981/82 academic session, when the Polytechnic adopted the faculty system, Civil Engineering became part of the Faculty of Construction and Environmental Technology alongside Building and Architectural as well as Land Surveying departments.
          </p>

          <p className="mt-4 text-justify leading-relaxed text-gray-700">
            The Department received provisional accreditation for the National Diploma (ND) and Higher National Diploma (HND) programmes in Structures and Transportation in 1985. Since its first accreditation, the Department has continued to maintain the established "Bida Standard" by addressing accreditation observations in line with National Board for Technical Education (NBTE) requirements. The Department is currently accredited to run ND and HND programmes, with HND options in Structures, Transportation and Water.
          </p>
        </motion.section>

        {/* HOD SPEECH */}
        <motion.section
          id="hodspeech"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="relative flex flex-col items-start gap-10 overflow-hidden rounded-2xl bg-white p-8 shadow-lg md:flex-row"
        >
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-blue-50/40 to-white" />

          <div className="relative z-10 flex w-full justify-center md:w-1/3">
            <motion.img
              src={hodProfile.image}
              alt={`${hodProfile.name}, Head of Department`}
              className="h-52 w-52 rounded-full border-4 border-white object-cover object-top shadow-xl"
              whileHover={{ rotate: 2, scale: 1.04 }}
              onError={(event) => {
                event.currentTarget.src = "/images/default-staff.jpg";
              }}
            />
          </div>

          <div className="relative z-10 flex-1">
            <h2 className="flex items-center gap-3 text-2xl font-bold">
              <Users className="text-blue-700" />
              HOD Speech
            </h2>
            <Divider />

            <div className="mb-6 flex flex-wrap gap-3">
              {["Civil Infrastructure", "Practical Engineering", "Sustainable Design", "Professional Development"].map((badge) => (
                <span
                  key={badge}
                  className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 shadow-sm"
                >
                  {badge}
                </span>
              ))}
            </div>

            <div className="mb-6 rounded-md border-l-4 border-blue-600 bg-blue-50 p-4 shadow-sm">
              <p className="font-medium italic text-blue-900">
                “Our commitment is to provide practical, innovative, and industry-focused Civil Engineering Technology education that develops skilled professionals for sustainable infrastructure and national development.”
              </p>
            </div>

            <p className="text-justify leading-relaxed text-gray-700">
              It is my pleasure to warmly welcome all prospective students and visitors to the Department of Civil Engineering Technology in The Federal Polytechnic, Bida. Choosing a course of study is an important decision that can shape your future, and Civil Engineering offers an exciting opportunity to build a career around creativity, innovation, problem-solving, and service to society.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              Civil Engineering plays a major role in the development of every nation. The roads we travel on, bridges that connect communities, buildings where we live and work, drainage systems, water supply networks, dams, airports, and many other essential facilities are made possible through the knowledge, skills, and dedication of civil engineering professionals.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              Our department is committed to providing students with quality education that combines sound theoretical knowledge with practical experience. As a Polytechnic, we place strong emphasis on hands-on learning, technical competence, innovation, and the ability to apply knowledge to real-life situations. Students are exposed to structural engineering, highway and transportation engineering, geotechnical engineering, water resources engineering, construction technology, surveying, engineering drawing, environmental engineering, and project management.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              Through classroom instruction, laboratory practicals, field exercises, workshops, technical projects, computer applications, and industrial training, our students are equipped with the skills required to become competent and confident professionals. The engineering profession is also changing rapidly, and modern civil engineers now work with computer-aided design, Building Information Modelling, Geographic Information Systems, modern surveying equipment, drones, and sustainable construction techniques.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              Civil Engineering is rewarding, but it requires discipline, commitment, creativity, patience, and a willingness to learn. We encourage our students to understand that education goes beyond obtaining a certificate: knowledge, competence, good character, professionalism, teamwork, integrity, responsibility, accuracy, safety consciousness, and ethical conduct are equally essential.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              Graduates have opportunities in construction companies, engineering consultancy firms, government ministries and agencies, transportation organisations, water corporations, property development companies, environmental organisations, and other public and private establishments. Graduates may also become entrepreneurs or further their education in specialised areas of engineering.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              To every prospective student, I encourage you to see Civil Engineering not merely as a course of study, but as an opportunity to acquire skills that can help you solve problems, transform communities, and contribute to national development. Welcome to the Department of Civil Engineering — where knowledge meets practice, ideas become reality, and today’s students are prepared to build tomorrow’s world.
            </p>

            <p className="mt-6 font-semibold text-blue-700">
              — {hodProfile.name}, {hodProfile.credentials}
            </p>
            <p className="text-gray-600">{hodProfile.role}</p>
            <p className="mt-1 text-sm text-gray-500">{hodProfile.qualification}</p>
          </div>
        </motion.section>

        {/* WHY STUDY */}
        <motion.section
          id="why"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-blue-900 p-8 text-white shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Award className="text-yellow-300" />
            Why Study Here?
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-blue-100">
            {whyStudyIntro}
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {studyReasons.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-xl border border-white/10 bg-white/5 p-5 transition hover:bg-white/10"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-800 shadow-sm">
                    <Icon size={20} />
                  </span>
                  <h3 className="mt-4 font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-6 rounded-xl border border-white/10 bg-white/10 p-5">
            <p className="text-justify leading-relaxed text-blue-50">
              {whyStudyClosing}
            </p>
          </div>
        </motion.section>

        {/* ADMISSION */}
        <motion.section
          id="admission"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <GraduationCap className="text-blue-700" />
            Admission Requirements
          </h2>
          <Divider />

          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-6">
              <h3 className="font-bold text-blue-950">National Diploma (ND)</h3>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-gray-700">
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Minimum of five O'Level credit passes at not more than two sittings.</li>
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Required subjects include Mathematics, English Language, Physics, Chemistry and any other science subject.</li>
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Applicants must satisfy the O'Level requirement and the prescribed JAMB (UTME) score for the admission year.</li>
              </ul>
            </div>

            <div className="rounded-xl border border-blue-100 bg-blue-50 p-6">
              <h3 className="font-bold text-blue-950">Higher National Diploma (HND)</h3>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-gray-700">
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Applicants must satisfy the relevant O'Level requirements.</li>
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Applicants must have obtained the relevant National Diploma (ND) certificate.</li>
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Applicants should comply with the Polytechnic's current HND screening, documentation and registration procedures.</li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* CAREERS */}
        <motion.section
          id="prospects"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <BriefcaseBusiness className="text-blue-700" />
            Job Prospects
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-700">
            {jobProspectsIntro}
          </p>

          <h3 className="mt-7 text-lg font-semibold text-blue-800">
            Career opportunities include:
          </h3>

          <div className="mt-4 grid gap-x-8 gap-y-3 md:grid-cols-2">
            {careerOpportunities.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-gray-700 transition hover:border-blue-100 hover:bg-blue-50/60"
              >
                <CheckCircle2
                  size={17}
                  className="mt-1 shrink-0 text-blue-700"
                />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="mt-7 rounded-xl border-l-4 border-blue-700 bg-blue-50 p-5">
            <p className="text-justify leading-relaxed text-gray-700">
              {jobProspectsClosing}
            </p>
          </div>
        </motion.section>

        {/* CERTIFICATIONS */}
        <motion.section
          id="certifications"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-blue-50 p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <ShieldCheck className="text-blue-700" />
            Certification/Professional Courses
          </h2>
          <Divider />

          <div className="space-y-4 text-justify leading-relaxed text-gray-800">
            <p>{professionalDevelopmentIntro}</p>
            <p>{professionalRegistration}</p>
            <p>{professionalAssociationNote}</p>
          </div>

          <h3 className="mt-8 text-lg font-semibold text-blue-900">
            Recommended Professional and Technical Courses
          </h3>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {professionalCourses.map((item) => (
              <article
                key={item.title}
                className="rounded-xl border border-blue-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                    <BadgeCheck size={18} />
                  </span>
                  <div>
                    <h4 className="font-semibold text-blue-900">
                      {item.title}
                    </h4>
                    <p className="mt-1 text-sm leading-6 text-gray-700">
                      {item.description}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </motion.section>

        {/* STAFF */}
        <motion.section
          id="staff"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-6 shadow-lg md:p-8"
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="flex items-center gap-3 text-2xl font-bold text-slate-900">
                <Users2 className="text-blue-700" />
                Meet Our Staff
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600 sm:text-base">
                Explore the academic and technical professionals contributing to Civil Engineering teaching, laboratory practice, applied research, field training and student development.
              </p>
            </div>

            <label className="relative block w-full lg:max-w-sm">
              <Search
                size={18}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={staffSearch}
                onChange={(event) => setStaffSearch(event.target.value)}
                placeholder="Search staff or specialization..."
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-4 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </div>
          <Divider />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {filteredStaff.map((staff) => {
              const name = fullStaffName(staff);
              const research = cleanArray(staff.researchInterests);
              const specialties = cleanArray(staff.specialties);

              return (
                <motion.button
                  type="button"
                  key={staff.id}
                  onClick={() => setSelectedStaff(staff)}
                  whileHover={{ y: -7 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label={`View academic profile of ${name}`}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition hover:border-blue-200 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  <div className="relative h-[310px] overflow-hidden bg-slate-100">
                    <img
                      src={staff.image}
                      alt={name}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      onError={(event) => {
                        event.currentTarget.src = "/images/default-staff.jpg";
                      }}
                    />
                    <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-slate-950 via-slate-950/65 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                      <h3 className="text-lg font-bold leading-tight">{name}</h3>
                      <p className="mt-1.5 text-sm font-medium text-blue-200">
                        {cleanText(staff.rank) || "Department Staff"}
                      </p>
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex min-h-[42px] items-start gap-2.5 text-sm text-slate-600">
                      <DraftingCompass size={17} className="mt-0.5 shrink-0 text-blue-700" />
                      <span className="line-clamp-2 leading-5">
                        {research[0] || specialties[0] || "Civil Engineering"}
                      </span>
                    </div>
                    <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-sm font-bold text-blue-700">
                      <span>View Academic Profile</span>
                      <ChevronRight size={19} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {filteredStaff.length === 0 && (
            <div className="rounded-xl bg-slate-50 p-8 text-center text-slate-600">
              No staff profile matches your search.
            </div>
          )}
        </motion.section>

        {/* COURSE STRUCTURE */}
        <motion.section
          id="courses"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Ruler className="text-blue-700" />
            Course Structure
          </h2>
          <Divider />

          <p className="text-sm leading-6 text-slate-600">
            The departmental write-up provides the following major course structure for the National Diploma (ND) and Higher National Diploma (HND) programmes.
          </p>

          <div className="mt-7 grid gap-7 lg:grid-cols-2">
            <div>
              <h3 className="text-xl font-bold text-blue-800">Major Course Structure (ND)</h3>
              <div className="mt-4 rounded-xl bg-slate-50 p-5">
                <ol className="columns-1 gap-8 space-y-1.5 text-sm text-slate-700 sm:columns-2">
                  {ndCourseStructure.map((course) => (
                    <li key={course} className="mb-1 break-inside-avoid">{course}</li>
                  ))}
                </ol>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-blue-800">Major Course Structure (HND)</h3>
              <div className="mt-4 rounded-xl bg-blue-50/70 p-5">
                <ol className="columns-1 gap-8 space-y-1.5 text-sm text-slate-700 sm:columns-2">
                  {hndCourseStructure.map((course) => (
                    <li key={course} className="mb-1 break-inside-avoid">{course}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </motion.section>

        {/* BROCHURE */}
        <motion.section
          id="brochure"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-blue-900 p-7 text-white shadow-lg md:p-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="flex items-center gap-3 text-2xl font-bold">
                <FileText />
                Civil Engineering Admission Brochure
              </h2>
              <p className="mt-2 text-sm leading-6 text-blue-100">
                A website-ready admission overview developed directly from the supplied Civil Engineering departmental write-up.
              </p>
            </div>

            <a
              href="/civil-engineering-brochure.pdf"
              download
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2 font-semibold text-blue-900"
            >
              <Download size={18} />
              Download PDF
            </a>
          </div>
          <Divider />

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {brochureCards.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-xl border border-white/10 bg-white/10 p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-4 font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100">{item.text}</p>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* GALLERY */}
        <motion.section
          id="gallery"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-6 shadow-lg md:p-8"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Construction className="text-blue-700" />
            Department Gallery
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">
            Explore Civil Engineering structures, field practice, construction activities, materials and infrastructure.
          </p>
          <Divider />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {galleryImages.map((image, index) => (
              <motion.button
                type="button"
                key={image.img}
                onClick={() => setSelectedGalleryImage({ ...image, index })}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-left shadow-sm transition hover:border-blue-200 hover:shadow-lg"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={`${CIVIL_IMAGE_BASE}/gallery/${image.img}`}
                    alt={image.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(event) => {
                      event.currentTarget.src = `${CIVIL_IMAGE_BASE}/civil-engineering-hero.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/5 to-transparent" />
                  <div className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-blue-700 shadow-lg">
                    <Maximize2 size={17} />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <p className="font-semibold">{image.title}</p>
                    <p className="mt-1 text-xs text-slate-200">Click to View</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.section>

        {/* CONTACT */}
        <motion.section
          id="contact"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <MapPin className="text-blue-700" />
            Contact the Department
          </h2>
          <Divider />

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-gray-700">
                <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
              </p>

              <p className="mt-3 flex items-center gap-2 text-gray-700">
                <Mail size={19} className="text-blue-700" />
                <a href={`mailto:${hodProfile.email}`} className="hover:text-blue-700">
                  {hodProfile.email}
                </a>
              </p>

              <p className="mt-3 flex items-center gap-2 text-gray-700">
                <Phone size={19} className="text-blue-700" />
                <a href="tel:+2348033566533" className="hover:text-blue-700">
                  {hodProfile.phone}
                </a>
              </p>

              <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-5">
                <h3 className="font-semibold text-blue-900">Department Contact</h3>
                <p className="mt-2 text-sm leading-6 text-blue-800">
                  Prospective students and other visitors may contact the Department directly using the official email address and telephone number above.
                </p>
              </div>
            </div>

            <div>
              <iframe
                title="Federal Polytechnic Bida location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus"
                width="100%"
                height="280"
                loading="lazy"
                className="rounded-md border-0 shadow-sm"
              />
            </div>
          </div>
        </motion.section>
      </main>

      <AnimatePresence>
        {selectedStaff && (
          <StaffAcademicProfileModal
            staff={selectedStaff}
            onClose={() => setSelectedStaff(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedGalleryImage && (
          <GalleryLightbox
            image={selectedGalleryImage}
            images={galleryImages}
            onClose={() => setSelectedGalleryImage(null)}
            onChange={setSelectedGalleryImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CivilEngineering;
